// Build epoch rows from checkpoint RPC (CPC fullnode has no suix_getEpochMetrics).

import type { Checkpoint } from '@mysten/sui.js/client';

export type CpcEpochSummary = {
	epoch: number;
	epochTotalTransactions: bigint;
	firstCheckpointId: string;
	lastCheckpointId: string;
	epochStartTimestamp: number | null;
	epochEndTimestamp: number | null;
	storageNetInflow: bigint | null;
	stakeRewards: bigint | null;
	isCurrent: boolean;
};

type GasSummary = Checkpoint['epochRollingGasCostSummary'];

function gasNetInflow(summary: GasSummary | null | undefined): bigint | null {
	if (!summary) return null;
	const inflow = BigInt(summary.storageCost ?? 0);
	const outflow = BigInt(summary.storageRebate ?? 0);
	return inflow - outflow;
}

/** Aggregate descending checkpoint pages into per-epoch summaries (newest epoch first). */
export function summarizeEpochsFromCheckpoints(
	checkpoints: Checkpoint[],
	targetEpochs: number,
): CpcEpochSummary[] {
	if (!checkpoints.length) return [];

	type Acc = {
		epoch: number;
		lastCheckpointId: string;
		firstCheckpointId: string;
		startTimestamp: number;
		endTotal: bigint;
		endTimestamp: number;
		endGas: GasSummary | null | undefined;
		txCountSum: number;
	};

	const results: CpcEpochSummary[] = [];
	let acc: Acc | null = null;

	const flush = (boundaryTotal: bigint | null, isCurrent: boolean) => {
		if (!acc) return;
		const txDelta =
			boundaryTotal != null && acc.endTotal >= boundaryTotal
				? acc.endTotal - boundaryTotal
				: BigInt(acc.txCountSum);
		results.push({
			epoch: acc.epoch,
			epochTotalTransactions: txDelta,
			firstCheckpointId: acc.firstCheckpointId,
			lastCheckpointId: acc.lastCheckpointId,
			epochStartTimestamp: acc.startTimestamp > 0 ? acc.startTimestamp : null,
			epochEndTimestamp: isCurrent ? null : acc.endTimestamp,
			storageNetInflow: gasNetInflow(acc.endGas),
			stakeRewards: null,
			isCurrent,
		});
	};

	for (const cp of checkpoints) {
		const epoch = Number(cp.epoch);
		const seq = cp.sequenceNumber;
		const total = BigInt(cp.networkTotalTransactions ?? 0);
		const ts = Number(cp.timestampMs ?? 0);

		if (!acc || acc.epoch !== epoch) {
			if (acc) {
				flush(total, false);
				if (results.length >= targetEpochs) break;
			}
			acc = {
				epoch,
				lastCheckpointId: seq,
				firstCheckpointId: seq,
				startTimestamp: ts,
				endTotal: total,
				endTimestamp: ts,
				endGas: cp.epochRollingGasCostSummary,
				txCountSum: cp.transactions?.length ?? 0,
			};
			continue;
		}

		acc.firstCheckpointId = seq;
		acc.txCountSum += cp.transactions?.length ?? 0;
		if (ts > 0) {
			acc.startTimestamp = ts;
			acc.endTimestamp = ts;
		}
	}

	if (acc && results.length < targetEpochs) {
		flush(null, true);
	}

	return results.slice(0, targetEpochs);
}

const EPOCH_DETAIL_MAX_PAGES = 200;
const EPOCH_DETAIL_PAGE_SIZE = 100;

/** Minimal client surface for checkpoint pagination (avoids @mysten version drift). */
export type CpcCheckpointClient = {
	getCheckpoints(input: {
		descendingOrder: boolean;
		cursor?: string | null;
		limit?: number;
	}): Promise<{
		data: Checkpoint[];
		hasNextPage: boolean;
		nextCursor?: string | null;
	}>;
};

/** Load one epoch by scanning sui_getCheckpoints (CPC fullnode has no suix_getEpochs). */
export async function fetchCpcEpochByNumber(
	client: CpcCheckpointClient,
	targetEpoch: number,
): Promise<CpcEpochSummary | null> {
	const collected: Checkpoint[] = [];
	let boundaryTotal: bigint | null = null;
	let cursor: string | null | undefined = null;

	for (let pages = 0; pages < EPOCH_DETAIL_MAX_PAGES; pages++) {
		const page = await client.getCheckpoints({
			descendingOrder: true,
			cursor,
			limit: EPOCH_DETAIL_PAGE_SIZE,
		});
		if (!page.data.length) break;

		let done = false;
		for (const cp of page.data) {
			const epoch = Number(cp.epoch);
			if (epoch > targetEpoch) continue;
			if (epoch < targetEpoch) {
				boundaryTotal = BigInt(cp.networkTotalTransactions ?? 0);
				done = true;
				break;
			}
			collected.push(cp);
		}
		if (done) break;
		if (!page.hasNextPage || !page.nextCursor) break;
		cursor = page.nextCursor;
	}

	if (!collected.length) return null;

	const lastCp = collected[0];
	const firstCp = collected[collected.length - 1];
	const endTotal = BigInt(lastCp.networkTotalTransactions ?? 0);
	const txCountSum = collected.reduce((n, cp) => n + (cp.transactions?.length ?? 0), 0);
	const txDelta =
		boundaryTotal != null && endTotal >= boundaryTotal
			? endTotal - boundaryTotal
			: BigInt(txCountSum);
	const startTs = Number(firstCp.timestampMs ?? 0);
	const endTs = Number(lastCp.timestampMs ?? 0);

	return {
		epoch: targetEpoch,
		epochTotalTransactions: txDelta,
		firstCheckpointId: firstCp.sequenceNumber,
		lastCheckpointId: lastCp.sequenceNumber,
		epochStartTimestamp: startTs > 0 ? startTs : null,
		epochEndTimestamp: boundaryTotal == null ? null : endTs > 0 ? endTs : null,
		storageNetInflow: gasNetInflow(lastCp.epochRollingGasCostSummary),
		stakeRewards: null,
		isCurrent: boundaryTotal == null,
	};
}
